import {AppParameter} from './app-parameter';
import {AppParameterMap} from './app-parameter-map';

type AppRouteSegment = (string | AppParameter);

export class AppRoute {
  private static readonly ROOT_SEGMENT = '';
  private static readonly VALID_SEGMENT_CHARACTERS = /^[a-z0-9\-/]+$/;

  private readonly segments: AppRouteSegment[];
  public readonly parameters: readonly AppParameter[];

  constructor(root: string, ...segments: (string | AppParameter)[]);
  constructor(parent: AppRoute, segment: string | AppParameter, ...segments: (string | AppParameter)[]);
  constructor(rootOrParent: string | AppRoute, ...segments: AppRouteSegment[]) {
    let newSegments: AppRouteSegment[];
    let newParams: AppParameter[];

    if (rootOrParent instanceof AppRoute) {
      newSegments = [...rootOrParent.segments];
      newParams = [...rootOrParent.parameters];
    } else {
      newSegments = [AppRoute.ROOT_SEGMENT];
      newParams = [];
      this.appendStringSegment(newSegments, rootOrParent);
    }

    segments.forEach(segment => {
      if (segment instanceof AppParameter) {
        this.appendParameterSegment(newSegments, newParams, segment);
      } else {
        this.appendStringSegment(newSegments, segment);
      }
    });

    this.segments = newSegments;
    this.parameters = newParams;
  }

  private appendStringSegment(existingSegments: AppRouteSegment[], segment: string): void {
    if (!segment || !AppRoute.VALID_SEGMENT_CHARACTERS.test(segment)) {
      throw new Error('AppRoute constructor requires non-empty string segments that contain only lowercase letters, numbers, hyphens, and forward slashes');
    }

    segment
      .split('/')
      .filter(subsegment => subsegment.length > 0)
      .forEach(subsegment => existingSegments.push(subsegment));
  }

  private appendParameterSegment(existingSegments: AppRouteSegment[], existingParams: AppParameter[], param: AppParameter) {
    existingParams.forEach(existingParam => {
      if (existingParam.name === param.name) {
        throw new Error('AppRoute constructor requires unique names for all AppParameters but "' + param.name + '" was added more than once');
      }
    });

    existingSegments.push(param);
    existingParams.push(param);
  }

  public toRoutePath(): string {
    return this.buildRoute(this.segments);
  }

  public toRoutePathRelativeTo(ancestor: AppRoute): string {
    if (!this.isAncestor(ancestor)) {
      throw new Error('AppRoute cannot build a route path relative to a non-ancestor AppRoute');
    }

    return this.buildRoute(this.segments.slice(ancestor.segments.length));
  }

  private buildRoute(segments: AppRouteSegment[]): string {
    return segments
      .map(segment => segment instanceof AppParameter ? ':' + segment.name : segment)
      .filter(segment => segment !== AppRoute.ROOT_SEGMENT)
      .join('/');
  }

  private isAncestor(parentRoute: AppRoute) {
    if (parentRoute.segments.length >= this.segments.length) {
      return false;
    }

    for (let i = 0; i < parentRoute.segments.length; ++i) {
      if (parentRoute.segments[i] !== this.segments[i]) {
        return false;
      }
    }

    return true;
  }

  public toRouterLinkCommands(appParameterMap: AppParameterMap): string[];
  public toRouterLinkCommands(...params: string[]): string[];
  public toRouterLinkCommands(appParameterMapOrString: undefined | AppParameterMap | string, ...params: string[]): string[] {
    if (!appParameterMapOrString) {
      return this.toRouterLinkCommandsByParams();
    }

    if (appParameterMapOrString instanceof AppParameterMap) {
      return this.toRouterLinkCommandsByAppParameterMap(appParameterMapOrString)
    }

    return this.toRouterLinkCommandsByParams(appParameterMapOrString, ...params);
  }

  private toRouterLinkCommandsByAppParameterMap(appParameterMap: AppParameterMap): string[] {
    if (this.hasNoParameters(this.segments, this.parameters)) {
      return this.segments;
    }

    return this.segments.map(segment => segment instanceof AppParameter ? appParameterMap.get(segment) : segment);
  }

  private toRouterLinkCommandsByParams(...params: string[]) {
    if (this.parameters.length !== params.length) {
      throw new Error('AppRoute expected ' + this.parameters.length + ' parameters but received ' + params.length);
    }

    if (this.hasNoParameters(this.segments, this.parameters)) {
      return this.segments;
    }

    params.forEach(param => {
      if (!param) {
        throw new Error('AppRoute parameter cannot be empty');
      }
    });

    let paramsIndex = 0;

    return this.segments.map(segment => segment instanceof AppParameter ? params[paramsIndex++] : segment);
  }

  private hasNoParameters(segments: AppRouteSegment[], requiredParams: readonly AppParameter[]): segments is string[] {
    return requiredParams.length === 0;
  }
}
